import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { formatINR, formatIndianDate } from '../../../utils/currency';

const ExportPanel = ({ isOpen = false, onClose, assets = [], metrics = {}, categories = [] }) => {
  const [exportConfig, setExportConfig] = useState({
    format: 'csv',
    includePricing: true,
    includeWarranty: true,
    includeLocation: true
  });
  const [exporting, setExporting] = useState(false);

  const formatOptions = [
    { value: 'csv', label: 'CSV Spreadsheet (Excel compatible)' },
    { value: 'json', label: 'JSON Data' },
    { value: 'print', label: 'Printable Executive Report (PDF/Print)' }
  ];

  const handleConfigChange = (key, value) => {
    setExportConfig(prev => ({ ...prev, [key]: value }));
  };

  const categoriesMap = categories.reduce((acc, cat) => {
    acc[cat.id] = cat.name;
    return acc;
  }, {});

  const handleExport = async () => {
    setExporting(true);

    try {
      const timestamp = new Date().toISOString().slice(0, 10);

      if (exportConfig.format === 'csv') {
        // Build CSV Data
        const headers = [
          'Asset ID',
          'Asset Name',
          'Model',
          'Category',
          'Department',
          'Status',
          'Location',
          'Purchase Date',
          'Purchase Price (INR)',
          'Warranty Expiration'
        ];

        const rows = assets.map(asset => {
          const catName = categoriesMap[asset.asset_category_id] || asset.category?.name || 'N/A';
          const price = parseFloat(asset.purchase_price) || parseFloat(asset.price) || 0;
          const name = asset.name || `${asset.brand || ''} ${asset.model || ''}`.trim() || 'Asset';

          return [
            `"${(asset.asset_id || '').replace(/"/g, '""')}"`,
            `"${name.replace(/"/g, '""')}"`,
            `"${(asset.model || '').replace(/"/g, '""')}"`,
            `"${catName.replace(/"/g, '""')}"`,
            `"${(asset.department || '').replace(/"/g, '""')}"`,
            `"${(asset.status || '').replace(/"/g, '""')}"`,
            `"${(asset.location || '').replace(/"/g, '""')}"`,
            `"${asset.purchase_date ? formatIndianDate(asset.purchase_date) : ''}"`,
            price,
            `"${asset.warranty_expiration ? formatIndianDate(asset.warranty_expiration) : ''}"`
          ].join(',');
        });

        const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `asset-analytics-export-${timestamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

      } else if (exportConfig.format === 'json') {
        const jsonData = JSON.stringify({
          generatedAt: new Date().toISOString(),
          currency: 'INR',
          summaryMetrics: {
            totalPortfolioValueINR: metrics.totalValue,
            totalAssetsCount: assets.length,
            utilizationPercentage: metrics.utilization,
            warrantyExpiringCount: metrics.warrantyExpiring,
            replacementDueCount: metrics.replacementDue
          },
          assets: assets.map(a => ({
            id: a.id,
            asset_id: a.asset_id,
            name: a.name,
            model: a.model,
            category: categoriesMap[a.asset_category_id] || a.category?.name || 'N/A',
            department: a.department,
            status: a.status,
            location: a.location,
            purchase_date: a.purchase_date,
            price_inr: parseFloat(a.purchase_price) || parseFloat(a.price) || 0,
            warranty_expiration: a.warranty_expiration
          }))
        }, null, 2);

        const blob = new Blob([jsonData], { type: 'application/json;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `asset-analytics-${timestamp}.json`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

      } else if (exportConfig.format === 'print') {
        // Open clean printable window
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Asset Analytics Executive Report - ${timestamp}</title>
                <style>
                  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 24px; color: #1e293b; }
                  h1 { margin-bottom: 4px; color: #0f172a; }
                  .meta { color: #64748b; font-size: 13px; margin-bottom: 24px; }
                  .kpis { display: flex; gap: 16px; margin-bottom: 28px; }
                  .kpi { flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; background: #f8fafc; }
                  .kpi-title { font-size: 12px; color: #64748b; text-transform: uppercase; font-weight: 600; margin-bottom: 4px; }
                  .kpi-value { font-size: 20px; font-weight: 700; color: #0f172a; }
                  table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 12px; }
                  th, td { border: 1px solid #e2e8f0; padding: 8px 12px; text-align: left; }
                  th { background: #f1f5f9; font-weight: 600; }
                  tr:nth-child(even) { background: #f8fafc; }
                  .currency { font-family: monospace; font-weight: 600; }
                  @media print { button { display: none; } }
                </style>
              </head>
              <body>
                <button onclick="window.print()" style="padding: 8px 16px; background: #2563eb; color: #fff; border: none; border-radius: 4px; cursor: pointer; margin-bottom: 16px;">
                  Print / Save as PDF
                </button>
                <h1>Asset Analytics Executive Report</h1>
                <div class="meta">Generated: ${new Date().toLocaleString('en-IN')} • Filtered Assets: ${assets.length}</div>
                
                <div class="kpis">
                  <div class="kpi">
                    <div class="kpi-title">Total Portfolio Value</div>
                    <div class="kpi-value">${formatINR(metrics.totalValue, false)}</div>
                  </div>
                  <div class="kpi">
                    <div class="kpi-title">Active Utilization</div>
                    <div class="kpi-value">${(metrics.utilization || 0).toFixed(1)}%</div>
                  </div>
                  <div class="kpi">
                    <div class="kpi-title">Warranty Expiring (30d)</div>
                    <div class="kpi-value">${metrics.warrantyExpiring || 0}</div>
                  </div>
                  <div class="kpi">
                    <div class="kpi-title">Replacement Due (4y+)</div>
                    <div class="kpi-value">${metrics.replacementDue || 0}</div>
                  </div>
                </div>

                <h3>Asset Inventory Details (${assets.length} items)</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Asset ID</th>
                      <th>Asset Name</th>
                      <th>Model</th>
                      <th>Category</th>
                      <th>Department</th>
                      <th>Status</th>
                      <th>Location</th>
                      <th>Price</th>
                      <th>Warranty Expiry</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${assets.map(a => `
                      <tr>
                        <td><b>${a.asset_id || ''}</b></td>
                        <td>${a.name || `${a.brand || ''} ${a.model || ''}`.trim() || 'Asset'}</td>
                        <td>${a.model || '-'}</td>
                        <td>${categoriesMap[a.asset_category_id] || a.category?.name || '-'}</td>
                        <td>${a.department || '-'}</td>
                        <td>${a.status || '-'}</td>
                        <td>${a.location || '-'}</td>
                        <td class="currency">${formatINR(parseFloat(a.purchase_price) || parseFloat(a.price) || 0, false)}</td>
                        <td>${a.warranty_expiration ? formatIndianDate(a.warranty_expiration) : '-'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </body>
            </html>
          `);
          printWindow.document.close();
        }
      }

      onClose?.();
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to generate export file.');
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-elevated overflow-hidden animate-in fade-in zoom-in-95">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border bg-muted/20">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              <Icon name="Download" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-card-foreground">Export Asset Analytics</h3>
              <p className="text-xs text-muted-foreground">Download live filtered reports</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="p-3 bg-primary/5 border border-primary/15 rounded-lg flex items-center justify-between text-xs">
            <div>
              <span className="text-muted-foreground">Scope:</span> <span className="font-semibold text-foreground">{assets.length} Filtered Assets</span>
            </div>
            <div>
              <span className="text-muted-foreground">Value:</span> <span className="font-bold text-primary">{formatINR(metrics.totalValue, true)}</span>
            </div>
          </div>

          <div>
            <Select
              label="Select Format"
              options={formatOptions}
              value={exportConfig.format}
              onChange={(value) => handleConfigChange('format', value)}
            />
          </div>

          <div className="text-xs text-muted-foreground bg-muted/40 p-3 rounded-lg border border-border">
            <Icon name="CheckCircle" size={14} className="text-success inline mr-1" />
            Includes Indian Rupee (₹) pricing, category, department, location and lifecycle dates.
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-2.5 p-4 border-t border-border bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleExport}
            loading={exporting}
            iconName="Download"
            iconPosition="left"
          >
            {exportConfig.format === 'print' ? 'Generate Report' : 'Download File'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;
